import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
} from "@angular/core";

@Component({
	selector: "app-number-counter",
	templateUrl: "./number-counter.component.html",
	styleUrls: ["./number-counter.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumberCounterComponent implements OnChanges {
	@Input() value = 0;
	@Output() valueChange = new EventEmitter<number>();

	@Input() min = 0;
	@Input() max: number | null = null;
	@Input() size: "md" | "lg" = "md";
	@Input() ariaLabel: string | null = null;
	@Input() disabled = false;

	editValue: string | null = null;

	get displayValue(): string {
		return this.editValue ?? String(this.value || 0);
	}

	get decrementDisabled(): boolean {
		return this.disabled || (this.value || 0) <= this.min;
	}

	get incrementDisabled(): boolean {
		return this.disabled || (this.max != null && (this.value || 0) >= this.max);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if ((changes["value"] || changes["min"] || changes["max"]) && this.editValue == null) {
			const clamped = this.clamp(this.value || 0);
			if (clamped !== this.value) {
				this.setValue(clamped);
			}
		}
	}

	decrement(): void {
		if (this.decrementDisabled) {
			return;
		}
		this.setValue((this.value || 0) - 1);
	}

	increment(): void {
		if (this.incrementDisabled) {
			return;
		}
		this.setValue((this.value || 0) + 1);
	}

	onFocus(event: FocusEvent): void {
		if (this.disabled) {
			(event.target as HTMLInputElement).blur();
			return;
		}
		this.editValue = String(this.value || 0);
		const input = event.target as HTMLInputElement;
		requestAnimationFrame(() => input.select());
	}

	onInput(event: Event): void {
		if (this.disabled) {
			return;
		}
		const input = event.target as HTMLInputElement;
		const digitsOnly = input.value.replace(/\D/g, "");
		input.value = digitsOnly;
		this.editValue = digitsOnly;
	}

	onKeydown(event: KeyboardEvent): void {
		if (this.disabled) {
			event.preventDefault();
			return;
		}
		if (event.key === "Enter") {
			event.preventDefault();
			(event.target as HTMLInputElement).blur();
			return;
		}

		const controlKeys = ["Backspace", "Delete", "Tab", "Escape", "ArrowLeft", "ArrowRight", "Home", "End"];
		if (controlKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
			return;
		}

		if (!/^\d$/.test(event.key)) {
			event.preventDefault();
		}
	}

	onBlur(): void {
		if (this.disabled) {
			this.editValue = null;
			return;
		}
		const raw = this.editValue ?? "";
		const parsed = raw === "" ? this.min : parseInt(raw, 10);
		this.setValue(this.clamp(Number.isNaN(parsed) ? this.min : parsed));
		this.editValue = null;
	}

	private clamp(value: number): number {
		let next = Math.max(this.min, value);
		if (this.max != null) {
			next = Math.min(next, this.max);
		}
		return next;
	}

	private setValue(value: number): void {
		const next = this.clamp(value);
		this.value = next;
		this.valueChange.emit(next);
	}
}
